'use client'

/**
 * WertungMaintenanceModal — Modal das erscheint wenn ein Pilot einen
 * Wertungs-/Antrags-Button klickt während der Wartungsmodus aktiv ist.
 *
 * R106.4.11 (Stephan-Spec 2026-06-08).
 *
 * Verwendung:
 *   const { isDisabled, reason, openIfDisabled } = useWertungMaintenance()
 *   <button onClick={async () => {
 *     if (await openIfDisabled()) return  // Modal erscheint, Aktion blockiert
 *     // ... normale Aktion
 *   }}>Klassen-Korrektur</button>
 *
 *   {/* Modal-Render irgendwo im Layout *\/}
 *   <WertungMaintenanceModal />
 */

import { useCallback, useEffect, useState } from 'react'
import { AlertTriangle, X } from 'lucide-react'

const STATUS_ENDPOINT = '/api/public/system-status'
const POLL_INTERVAL_MS = 60_000

type SystemStatus = {
  wertungChangesDisabled: boolean
  wertungChangesReason: string
}

let cachedStatus: SystemStatus | null = null
let listeners = new Set<(s: SystemStatus) => void>()
let pollTimer: ReturnType<typeof setInterval> | null = null
let modalOpenSetter: ((open: boolean) => void) | null = null

async function fetchStatus(): Promise<SystemStatus | null> {
  try {
    const r = await fetch(STATUS_ENDPOINT, { cache: 'no-store' })
    if (!r.ok) return null
    return await r.json() as SystemStatus
  } catch {
    return null
  }
}

function notifyListeners(s: SystemStatus) {
  cachedStatus = s
  listeners.forEach(fn => fn(s))
}

function ensurePolling() {
  if (pollTimer) return
  // Initial fetch + poll alle 60 s
  fetchStatus().then(s => { if (s) notifyListeners(s) })
  pollTimer = setInterval(async () => {
    const s = await fetchStatus()
    if (s) notifyListeners(s)
  }, POLL_INTERVAL_MS)
}

export function useWertungMaintenance() {
  const [status, setStatus] = useState<SystemStatus | null>(cachedStatus)

  useEffect(() => {
    ensurePolling()
    const fn = (s: SystemStatus) => setStatus(s)
    listeners.add(fn)
    if (cachedStatus) setStatus(cachedStatus)
    return () => { listeners.delete(fn) }
  }, [])

  const openIfDisabled = useCallback(async (): Promise<boolean> => {
    // Frischer Status (im Fall der Cache schon stale ist)
    const fresh = await fetchStatus()
    if (fresh) notifyListeners(fresh)
    const current = fresh ?? cachedStatus
    if (current?.wertungChangesDisabled && modalOpenSetter) {
      modalOpenSetter(true)
      return true
    }
    return false
  }, [])

  return {
    isDisabled: status?.wertungChangesDisabled ?? false,
    reason: status?.wertungChangesReason ?? '',
    openIfDisabled,
  }
}

export function WertungMaintenanceModal() {
  const [open, setOpen] = useState(false)
  const { reason, isDisabled } = useWertungMaintenance()

  useEffect(() => {
    modalOpenSetter = setOpen
    return () => { modalOpenSetter = null }
  }, [])

  // Auto-schliessen wenn Wartungsmodus deaktiviert wurde
  useEffect(() => {
    if (!isDisabled && open) setOpen(false)
  }, [isDisabled, open])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-[1000] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false) }}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="rounded-2xl max-w-md w-full p-6 shadow-2xl"
        style={{
          background: 'var(--card, #14202a)',
          border: '2px solid rgba(239,68,68,0.55)',
          boxShadow: '0 0 60px rgba(239,68,68,0.25)',
        }}
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="shrink-0 w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)' }}>
            <AlertTriangle className="w-6 h-6 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-black text-lg uppercase tracking-wide" style={{ color: 'var(--text, #fff)' }}>
              Anträge aktuell pausiert
            </h3>
            <p className="text-xs uppercase tracking-[0.18em] mt-0.5" style={{ color: 'var(--muted, #94a3b8)' }}>
              Wartungsmodus aktiv
            </p>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="shrink-0 p-1.5 rounded-lg hover:bg-white/10 transition-colors"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" style={{ color: 'var(--muted, #94a3b8)' }} />
          </button>
        </div>

        <p
          className="text-sm leading-relaxed mb-5 whitespace-pre-line"
          style={{ color: 'var(--text, #e2e8f0)' }}
        >
          {reason || 'Aktuell können keine Anträge zu Wertung oder Geländetyp entgegengenommen werden — wir kennen den Bug und beheben ihn gerade. Im Anschluss werden alle Flüge neu berechnet.'}
        </p>

        <button
          type="button"
          onClick={() => setOpen(false)}
          className="w-full py-3 rounded-xl font-bold text-sm transition-colors"
          style={{
            background: 'var(--accent, #f2c94e)',
            color: 'var(--accent-text, #14202a)',
          }}
        >
          Verstanden
        </button>
      </div>
    </div>
  )
}
